import type { BlobLike } from "@pdfking/compatibility"
import type { PageNumber } from "@pdfking/renderer-api"
import type { Engine, TrackedTask } from "@pdfking/taskmaker"
import type { AsyncSequence, Bytes, Time } from "@pdfking/tfunc"
import type { PdfEngineOptions } from "../types/engine/PdfEngineOptions"
import type { GeneratedFile } from "../types/files/cached/GeneratedFile"
import type { RemoteFile } from "../types/files/cached/RemoteFile"
import type { VirtualFile } from "../types/files/VirtualFile"
import type { EngineTasks } from "../types/tasks/EngineTasks"
import { FileCache } from "@pdfking/compatibility"
import { Ceil } from "@pdfking/math"
import {
  FilterAsync,
  First,
  FullMethodDispatcher,
  Items,
  Peek,
  SafePromise,
  UnixTime,
  Void,
} from "@pdfking/tfunc"
import { EngineError } from "../errors/EngineError"
import { FileContentGUID } from "../methods/FileContentGUID"
import { FileContentSize } from "../methods/FileContentSize"
import { ErrorName } from "../types/errors/ErrorName"
import { VirtualFileType } from "../types/files/VirtualFileType"

export type FileBound = {
  readonly modified: Time
  readonly size: Bytes
  readonly stream: AsyncSequence<BlobLike>
}

type Emit = (task: TrackedTask) => void

const evict = (file?: VirtualFile) => ({
  cache_evict: true,
  task_id: file ? FileContentGUID(file) : undefined,
})

const dispatch = FullMethodDispatcher<
  [VirtualFile, Engine<EngineTasks>, PdfEngineOptions, Emit],
  Promise<FileBound>
>({
  [VirtualFileType.GENERATED_FILE]: async (file, _engine, options, _emit) => {
    const blob = await FileCache(options.file_cache).get(FileContentGUID(file))

    if (!blob) {
      console.error("Cache miss for file", file)
      throw EngineError(ErrorName.CACHE_EXPIRED)
    }

    return {
      modified: file.modified,
      size: blob.size as Bytes,
      stream: [blob],
    }
  },
  [VirtualFileType.MULTI_PAGE_PDF]: (
    file,
    engine,
    options,
    emit
  ): Promise<FileBound> =>
    dispatchWithFallback(file.content, engine, options, emit, () =>
      First(
        engine.TaskMerge(
          { options, ...evict(file) },
          file.pages.map(p => ({
            content: p,
            page_count: p.page_count,
            page_number: p.page_number,
          }))
        )
      )
    ),

  [VirtualFileType.REMOTE_FILE]: async (file, engine, options, emit) => {
    const { error, stream, value } = await Peek(
      engine.TaskDownload({ options, source: file, ...evict() })
    )

    if (error) throw error

    const generator = async function* () {
      for await (const chunk of stream) {
        const { stream } = await RequestBlob(engine, options, chunk, emit)
        yield* stream
      }
    }

    return {
      modified: UnixTime(),
      size: value.bytes_total,
      stream: generator(),
    }
  },

  [VirtualFileType.REMOTE_FILE_CHUNK]: (
    file,
    engine,
    options,
    emit
  ): Promise<FileBound> =>
    dispatchWithFallback(file.content, engine, options, emit, () =>
      First(
        FilterAsync(
          f => f.bytes_start === file.bytes_start,
          engine.TaskDownload({ options, source: file.remote, ...evict() })
        )
      )
    ),

  [VirtualFileType.SINGLE_PAGE_IMAGE]: (file, engine, options, emit) =>
    dispatchWithFallback(file.content, engine, options, emit, () =>
      First(
        file.channel
          ? FilterAsync(
              f => f.channel === file.channel,
              engine.TaskSeparate({
                options,
                source: file.source,
                ...evict(),
              })
            )
          : engine.TaskRasterize({
              options,
              source: file.source,
              ...evict(),
            })
      )
    ),

  [VirtualFileType.SINGLE_PAGE_PDF]: (
    file,
    engine,
    options,
    emit
  ): Promise<FileBound> =>
    dispatchWithFallback(file.content, engine, options, emit, () => {
      if (file.source) {
        // compute a large batch, ideally identical to the one SplitPdf does so it gets cached
        const max = options.max_split_pages
        const batch = Ceil(file.page_number / max) - 1
        return First(
          FilterAsync(
            f => f.page_number === file.page_number,
            engine.TaskSplit({
              options,
              source: file.source,
              start_page: (batch * max + 1) as PageNumber,
              end_page: ((batch + 1) * max) as PageNumber,
              ...evict(),
            })
          )
        )
      }
      return First(
        engine.TaskPlot({
          options,
          page: file.page,
          context: file.context,
          ...evict(file),
        })
      )
    }),

  [VirtualFileType.ZIP_FILE]: (
    file,
    engine,
    options,
    emit
  ): Promise<FileBound> =>
    dispatchWithFallback(file.content, engine, options, emit, () =>
      First(
        engine.TaskZip(
          { options, ...evict(file) },
          Items(file.files).map(([name, content]) => ({ name, content }))
        )
      )
    ),
})

const dispatchWithFallback = async (
  file: GeneratedFile | RemoteFile,
  engine: Engine<EngineTasks>,
  options: PdfEngineOptions,
  emit: Emit = Void,
  fallback: () => Promise<VirtualFile>
): Promise<FileBound> => {
  const { error, value } = await SafePromise(
    dispatch(file, engine, options, emit)
  )

  if (error) {
    const computed = await fallback()

    if (FileContentGUID(computed) === FileContentGUID(file)) {
      return dispatch(computed, engine, options, emit)
    }

    if (FileContentSize(computed) === FileContentSize(file)) {
      console.error("⚠️ sha256 failed, size passed", { computed, file })

      return dispatch(computed, engine, options, emit)
    }

    throw EngineError(ErrorName.CACHE_MISMATCH)
  }

  return value
}

export const RequestBlob = (
  engine: Engine<EngineTasks>,
  options: PdfEngineOptions,
  file: VirtualFile,
  emit: Emit = Void
): Promise<FileBound> => dispatch(file, engine, options, emit)
